/**
 * socratesScript — guion conversacional de Sócrates para la lección líquida.
 * Frases cercanas, con humor cotidiano, nunca robóticas. {tema} se sustituye
 * por el título de la lección.
 */
import { GUION_CA } from "./guionCatalan.mjs";

// EL SALUDO QUE ENSEÑA LA PORTADA, con nombre propio y fuera de la lista por un motivo: en la
// lección el saludo se elige al azar entre los cuatro, y un escaparate que cambia de frase en
// cada recarga no es un escaparate. Este es el que eligió Gabri el 19 de octubre de 2026.
// Se declara UNA vez y entra en la lista: así la portada no copia un texto que vive aquí, y el
// azar de la lección lo sigue incluyendo.
export const SALUDO_DE_LA_PORTADA = "¡Buenas! Ya estaba deseando que llegaras.";

// saludo amistoso al abrir la lección (siempre, antes del enganche)
export const WELCOMES = [
  "¡Hola! Qué alegría verte por aquí. 👋",
  SALUDO_DE_LA_PORTADA,
  "¡Hola de nuevo! Ponte cómodo, que hoy traigo algo bueno.",
  "¡Hey! Me alegro de verte. Vamos allá.",
];

// refuerzo positivo al acertar una pregunta
export const PRAISE = [
  "¡Correcto! 🎯",
  "¡Eso es! Muy bien.",
  "¡Bien ahí! Sigue así.",
  "¡Exacto! Lo tienes dominado.",
  "¡Genial! Esa era.",
  "¡Boom! Ni una duda.",
];

export const GREETINGS = [
  "Venga, vamos a hablar de un tema muy interesante hoy. ¿Qué sabes acerca de «{tema}»?",
  "Hoy le hincamos el diente a algo chulo. Antes de nada, cuéntame: ¿qué sabes de «{tema}»?",
  "Sin presión, ¿eh? Solo por curiosidad: ¿qué se te viene a la cabeza cuando digo «{tema}»?",
  "Empecemos por ti. ¿Qué es lo que ya sabes sobre «{tema}»? Aunque sea una intuición.",
];

/**
 * ACUSE DE RECIBO TRAS LO QUE CUENTA EL ALUMNO. **No es un juicio, y antes lo era.**
 *
 * Hasta el 26 de septiembre de 2026 esto se llamaba «juicio afectivo» y decía cosas como «¡Vas
 * muy bien encaminado!» o «¡Bastante bien!». El problema no es que se equivocara: es que
 * **nadie lee esa respuesta**. `LessonChat.jsx` la recoge y saca de aquí una frase al azar, así
 * que el elogio era exactamente igual de cierto escribiera lo que escribiera el alumno.
 *
 * En clase se vio lo que eso vale: alguien contestó «hola, guapo» y Sócrates le dijo que iba
 * bien encaminado, delante de todos. Un mentor que aprueba cualquier cosa enseña en diez
 * segundos que lo que escribas no se lee, y eso se lleva por delante todas las preguntas
 * abiertas del curso.
 *
 * Estas frases dicen lo único que ES verdad sin haber leído: que se ha recibido y que se sigue.
 * El día que haya alguien leyendo de verdad, volverán los elogios y se los habrán ganado.
 *
 * Y CADA FRASE ACUSA Y ENLAZA A LA VEZ, desde el 5 de octubre de 2026. Hasta hoy el chat mandaba
 * DOS mensajes seguidos —una de aquí y otra de una lista `BRIDGE_TO_THEORY` que ya no existe— y
 * las dos decían lo mismo: «Bien, ya tengo por dónde empezar» y, debajo, «Con eso ya tenemos por
 * dónde empezar. Te cuento un poco más». Lo cazó Gabri haciendo la primera lección de Psicología:
 * *«casi responde con el mismo comentario dos veces»*.
 *
 * No era mala suerte del azar: eran dos listas escritas en momentos distintos que acabaron
 * cubriendo el mismo trabajo, y con cinco por tres hay quince parejas de las que ninguna aporta
 * nada en el segundo renglón. Una sola frase que acuse y abra la teoría hace las dos cosas y gasta
 * una burbuja en vez de dos, que además es lo que más pesa en el ritmo.
 */
export const RECOGNITIONS = [
  "Vale, ya sé de dónde partimos. Sobre esa base, te cuento lo demás.",
  "Gracias, con eso me hago una idea. Déjame añadir un par de cosas.",
  "Apuntado. A ver qué cambia de aquí al final, que para eso empezamos.",
  "Bien, ya tengo por dónde empezar. Te cuento un poco más.",
  "Perfecto, eso me sirve. Vamos con lo que se sabe de esto.",
];

/**
 * CUANDO LO QUE LLEGA NO ES UNA RESPUESTA —un saludo, un emoji, dos letras—. Se pregunta otra
 * vez, UNA sola, con humor y sin regañar: quien contesta en broma tiene más aguante que
 * cualquier bucle, y un alumno atrapado en la puerta de la lección no aprende nada.
 * Quién decide que no es una respuesta: `lib/respuestaInicial.mjs`.
 */
export const REPREGUNTAS = [
  "Jajaja. Pero va en serio: aunque sea una intuición, ¿qué se te viene a la cabeza?",
  "Eso no me dice mucho 😄. Dime lo que sea, aunque creas que no tiene nada que ver.",
  "Te leo, pero necesito algo más. Una palabra suelta me vale.",
];

/**
 * Y CUANDO DICE QUE NO SABE. **Es una respuesta legítima** a «¿qué sabes de esto?», y tratarla
 * como una falta sería castigar justo la honestidad que se le está pidiendo.
 */
export const SIN_IDEA = [
  "Pues mejor: así todo lo que viene ahora es nuevo. Vamos allá.",
  "Perfecto, para eso estamos. Empezamos de cero.",
  "Me vale, y es más honesto que inventarse algo. Te cuento.",
];

// AQUI VIVIA `BRIDGE_TO_THEORY`, y se borro el 5 de octubre de 2026 en vez de arreglarse. Eran
// tres frases que se mandaban SIEMPRE justo despues del acuse de recibo, y decian su mismo
// trabajo: «Con eso ya tenemos por donde empezar» debajo de «Bien, ya tengo por donde empezar».
// El puente no se ha perdido, se ha mudado: ahora va en la propia frase del acuse, arriba.
//
// No se deja la lista vacia ni comentada. Una constante que no usa nadie es la que alguien
// vuelve a enchufar dentro de seis meses sin saber por que se habia apagado.

// halago intelectual al interrumpir
export const FLATTERIES = [
  "¡Qué gran pregunta!",
  "Me gusta cómo piensas...",
  "Fíjate que esa es la clave de todo...",
  "Uf, has tocado hueso con eso.",
  "Buenísima. Justo ahí está lo interesante.",
];

// gancho para volver a la lección tras la duda
export const RETURN_HOOKS = [
  "...pero no nos adelantemos demasiado. ¿Seguimos con lo que estábamos viendo?",
  "...y de hecho, eso conecta perfectamente con lo siguiente que te iba a contar.",
  "Dicho esto, ¿listo para continuar?",
  "Te lo enlazo con lo de antes en cuanto sigamos, ya verás.",
];

// respaldo si la IA no está disponible
export const FALLBACK_ANSWERS = [
  "Muy buena duda. La respuesta corta es que depende del contexto, pero tranquilo: lo vas a ver clarísimo en cuanto sigamos con el temario.",
  "Me encanta que preguntes eso. Guárdate la idea un segundo, porque justo lo siguiente que te iba a contar la responde casi entera.",
];

export const PRE_ACTIVITY = [
  "Voy a ponerte a prueba con unas preguntas, a ver qué tal te va.",
  "Ahora te toca a ti. Unas preguntitas rápidas para ver cómo vas.",
];

/**
 * LA ULTIMA PREGUNTA, Y LA HACE EL MENTOR. Entra el 5 de octubre de 2026 con la nota de Gabri
 * sobre la primera lección de Psicología: la lección terminaba y lo último que pasaba era una
 * reflexión que *«despierta tu curiosidad pero no sigue la conversación»*. Su propuesta, literal:
 * *«quizás estaría bien poner: te ha quedado alguna pregunta de lo que hemos visto»*.
 *
 * ES UNA SOLA, Y ESO NO ES TACAÑERÍA: *«quizás una sola respuesta ya está bien, para no gastar
 * peticiones de IA»*. Preguntar es voluntario y responder cuesta una llamada, así que el que no
 * tenga dudas se va sin gastar nada y el que las tenga se lleva una respuesta de verdad.
 */
export const CIERRE_DUDA = [
  "Antes de irnos: ¿te ha quedado alguna pregunta de lo que hemos visto?",
  "Una última cosa. ¿Hay algo de lo de hoy que te haya quedado a medias?",
  "¿Te queda alguna duda de esto? Pregúntame y te la resuelvo antes de cerrar.",
];

export const FAREWELL =
  "Bueno, creo que necesito un refresco y unas patatitas bien ricas. Si quieres, vente, que yo invito. Has hecho un buen trabajo.";

export const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const fill = (tpl, tema) => tpl.replace(/\{tema\}/g, tema || "esto");

// EL AVISO DE QUE VIENEN PREGUNTAS, que antes vivia escrito dentro de `LessonChat.jsx`.
const aviso = (n) => (n === 1
  ? "Aviso: ahora viene una pregunta y la teoría se para aquí."
  : `Aviso: ahora vienen ${n} preguntas y la teoría se para aquí.`);

// EL GUION ENTERO, EN EL IDIOMA DE LA LECCION. Lo decide `idiomaDeLaLeccion` (en
// `lib/cierreDeConceptos.mjs`) mirando el texto, y el catalan vive en `guionCatalan.mjs`.
const GUION_ES = {
  WELCOMES, PRAISE, GREETINGS, RECOGNITIONS, REPREGUNTAS, SIN_IDEA, FLATTERIES, RETURN_HOOKS,
  FALLBACK_ANSWERS, PRE_ACTIVITY, CIERRE_DUDA, FAREWELL, aviso,
  REPASA: "Repasa los conceptos clave. Cuando los hayas visto todos, seguimos.",
};
export const guionPara = (idioma) => (idioma === "ca" ? GUION_CA : GUION_ES);
